import { useState, useEffect, useCallback } from 'react';
import { batchedUpdates } from '@/common/utils';
import env from '@/env';

let duccConfigRequest: any = null;

async function fetchDuccConfig() {
  if (duccConfigRequest) return await duccConfigRequest;
  duccConfigRequest = fetch(
    env.DUCC_DOMAIN + `?body={"channel":"${env.DUCC_CHANNEL}","type":0}&appid=yhd_imgUpload&functionId=commonConfig`
  ).then((res) => res.json());
  return await duccConfigRequest;
}

// pub和sub 一个发布订阅对象
const ps = {
  _listeners: [] as any,
  pub(...args: any) {
    ps._listeners.forEach((item: any) => {
      item(...args);
    });
  },
  sub(cb: any) {
    ps._listeners.push(cb);

    return () => {
      ps._listeners = ps._listeners.filter((item: any) => item !== cb);
    };
  },
};

let duccData: any = {
  status: 'initial',
  response: null,
};

function setDuccData(data: any) {
  duccData = data;
  ps.pub(duccData);
}

function parseResponse(res: Record<string, unknown>) {
  return Object.keys(res).reduce<Record<string, unknown>>((resault, key) => {
    const val = res[key];
    // 如果是JSON字符串直接解析
    if (typeof val === 'string' && val[0] === '{' && val[val.length - 1] === '}') {
      try {
        resault[key] = JSON.parse(val);
      } catch (ex) {
        console.error('配置解析出错', ex);
        resault[key] = val;
      }
    } else {
      resault[key] = res[key];
    }
    return resault;
  }, {});
}

function innerGetConfig(config: Record<string, any> | null, keys: string[], defaultValue: any): any {
  // 如果找到了空，返回默认值
  if (config == null) return defaultValue;
  // 找到后返回对应值
  if (keys.length === 0) return config;

  // 递归寻找key对应值
  return innerGetConfig(config[keys[0]], keys.slice(1), defaultValue);
}

function useConfig(): [(keys: string | string[], defaultValue?: any, transformValue?: any) => any, any] {
  const [config, setConfig] = useState<Record<string, any> | null>(duccData.response);

  useEffect(() => {
    // 之前已经加载过，并且加载成功
    if (duccData.status === 'success') {
      setConfig(duccData.response);
      return;
    }

    // 单例，如果有请求进行中，就等待成功后响应变化即可
    const rmSub = ps.sub((a: any) => {
      if (duccData.status === 'loading') return;
      if (duccData.status === 'success') setConfig(duccData.response);
      // 无论成功失败都清除监听
      rmSub();
    });

    return rmSub;
  }, []);

  useEffect(() => {
    // 失败和初始化都要再加载
    if (duccData.status === 'success' || duccData.status === 'loading') return;

    setDuccData({ ...duccData, status: 'loading' });

    fetchDuccConfig()
      .then((res) => {
        if (res.ret !== '0') {
          // 不成功设置成初始状态，下次执行重新加载
          setDuccData({ ...duccData, status: 'error' });
        } else {
          batchedUpdates(() => {
            setDuccData({
              status: 'success',
              response: parseResponse(res.data),
            });
          });
        }
      })
      .catch((ex) => {
        // 不成功设置成初始状态，下次执行重新加载
        setDuccData({ ...duccData, status: 'error' });
      });
  }, []);

  const getConfig = useCallback(
    (keys: string | string[], defaultValue?: any, transformValue = (val: any) => val) => {
      return transformValue(innerGetConfig(config, Array.isArray(keys) ? keys : [keys], defaultValue));
    },
    [config]
  );

  return [getConfig, config];
}

export { useConfig, fetchDuccConfig };
